import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useMemo, useState } from 'react'
import { ContentCardProps } from 'src/components/ContentCard/ContentCard'
// import { ExpertsCarousel } from 'src/components/ExpertsCarousel/ExpertsCarousel'
import { Footer } from 'src/components/Footer/Footer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { PremiumModal } from 'src/components/global/PremiumModal'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
// import { PremiumSubTopics } from 'src/components/PremiumSubTopics/PremiumSubTopics'
// import { SubTopicSection } from 'src/components/SubTopicSection/SubTopicSection'
import { apolloSdk } from 'src/graphql/apolloSdk'
import {
  IContentListItemFragment,
  IEnum_Content_Contenttype,
  ISubTopicItemFragment,
  ITopicItemFragment,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { normalizeWritersList } from 'src/utils/normalizeContentWriters'

const PremiumSubTopics = dynamic(
  () => import('src/components/PremiumSubTopics/PremiumSubTopics').then((mod) => mod.PremiumSubTopics),
  {
    loading: () => <p className="loading-placeholder"></p>,
    ssr: false,
  },
)
const SubTopicSection = dynamic(
  () => import('src/components/SubTopicSection/SubTopicSection').then((mod) => mod.SubTopicSection),
  {
    loading: () => <p className="loading-placeholder"></p>,
    ssr: false,
  },
)
const ExpertsCarousel = dynamic(
  () => import('src/components/ExpertsCarousel/ExpertsCarousel').then((mod) => mod.ExpertsCarousel),
  {
    loading: () => <p className="loading-placeholder"></p>,
    ssr: false,
  },
)

const isTopicItemFragment = (category: ITopicItemFragment | ISubTopicItemFragment): category is ITopicItemFragment =>
  'subTopics' in (category || {})

type Props = {
  premiumCategories: {
    category: ITopicItemFragment | ISubTopicItemFragment
    contents: IContentListItemFragment[]
  }[]
}

const SolutionsGuidesPage: NextPage<Props> = ({ premiumCategories }) => {
  const [showModal, setShowModal] = useState(false)
  const router = useRouter()

  const { isVendor } = useAuthenticated()
  const handleCloseModal = () => {
    setShowModal(false)
  }
  const categories = useMemo(
    () =>
      premiumCategories
        .filter(({ contents }) => contents.length > 0)
        .map(({ category, contents }) => {
          // Filter out competitive analysis articles for vendors
          let filteredContents = contents
          if (isVendor) {
            filteredContents = contents.filter(
              (content) => content?.contentType !== IEnum_Content_Contenttype.CompetitiveAnalysis,
            )
          }

          // Ensure exactly 3 articles are shown
          const limitedContents = filteredContents.slice(0, 3)

          return {
            category,
            contents: limitedContents.map((content) => {
              const writers = normalizeWritersList(content?.writer)
              const item: ContentCardProps = {
                title: content?.title || '',
                snippet: content?.snippet,
                writer: writers.length > 0 ? writers : null,
                url: `/contents/${content?.slug}/`,
              }
              return item
            }),
          }
        }),
    [premiumCategories, isVendor],
  )
  const onCardSelect = (url: string) => {
    // if (isPremium) {
    router.push({ pathname: url })
    // } else {
    //   setShowModal(true)
    // }
  }
  const meta = <Meta title={'Solution Guides'} url={`/solution-guides`} description="This is solutions guides" />
  return (
    <Main meta={meta}>
      {showModal && <PremiumModal origin="/solution-guides/" isOpen={showModal} onClose={handleCloseModal} />}

      <MainContainer id="mainContainer">
        <div className="flex flex-col bg-clouds">
          {/* <Breadcrumb
            className="justify-start"
            subject="INDUSTRY ANALYSIS"
            subjectRoute={`/`}
            subtitle="SOLUTION GUIDES"
          /> */}
          <div className="px-5 md:pl-0 !max-w-[1100px] w-full mx-auto flex flex-row items-center pt-2">
            <div className="flex-row flex mb-[3rem] md:w-2/3">
              <div className="flex flex-col" style={{ justifyContent: 'center' }}>
                <h1 className="pt-[0.5rem] pb-[0.625rem] text-[1.875rem] font-bold font-secondary text-darkgray">
                  Solution Guides
                </h1>
                <h3 style={{ fontWeight: 450 }} className=" text-[0.9375rem] font-primary font-regular text-darkgray">
                  Resources by our experts on each of our premium categories, landscapes, functional requirements,
                  evaluation criteria and much more
                </h3>
              </div>
            </div>
          </div>
        </div>
        <div
          className="px-5 md:pl-0 !max-w-[1100px] w-full mx-auto flex flex-col items-center pt-10 mb-[4rem]"
          style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <div className="pt-[0.5rem] pb-[0.625rem] text-[.875rem] font-medium font-secondary text-darkgray">
            PREMIUM CATEGORIES
          </div>
          <PremiumSubTopics
            className="mt-[.5rem]"
            premiumCategories={premiumCategories?.map((item) => item.category)}
          />
          <div className="!max-w-[1100px] flex flex-col justify-start mx-auto">
            {categories.map(({ category, contents }) => {
              const topic = category
              if (!topic) return null
              const url = isTopicItemFragment(topic)
                ? `/topics/${topic?.slug}/?tab=content`
                : `/topics/${topic?.topics?.[0]?.slug}/${category?.slug}/?tab=content`
              return (
                <SubTopicSection contents={contents} url={url} title={topic?.name || ''} onSelectCard={onCardSelect} />
              )
            })}
          </div>
        </div>
        <ExpertsCarousel />
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default SolutionsGuidesPage

export const getStaticProps: GetStaticProps<any> = async () => {
  try {
    const [{ topics }, { subTopics }] = await Promise.all([
      apolloSdk.premiumTopicsWithContents(),
      apolloSdk.premiumSubTopicsWithContents(),
    ])
    /* const premiumTopics: {
    category: ITopicItemFragment | ISubTopicItemFragment
    contents: IContentListItemFragment[]
  }[] = (topics?.data || []).map((topic) => ({
    category: topic,
    contents: (topic?.attributes?.contents?.data || []).slice(0, 3),
  })) */
    /* const premiumSubTopics: {
    category: ITopicItemFragment | ISubTopicItemFragment
    contents: IContentListItemFragment[]
  }[] = (subTopics?.data || []).map((subTopic) => ({
    category: subTopic,
    contents: (subTopic?.attributes?.contents?.data || []).slice(0, 3),
  })) */

    const premiumTopics = topics?.map((topic) => ({
      category: topic,
      contents: topic?.contents || [],
    }))
    const premiumSubTopics = subTopics.map((subTopic) => ({
      category: subTopic,
      contents: subTopic?.contents || [],
    }))

    return {
      props: {
        premiumCategories: [...(premiumTopics || []), ...(premiumSubTopics || [])], // premiumTopics.concat(premiumSubTopics),
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Solution-Guides:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 5, // Reattempt fetching during the next static regeneration
    }
  }
}
